import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { picturesAndDescriptions } from './pictureKeys';

export type PictureAndDescription = {
  path: string;
  key: string;
  description: string;
};

export type Message = {
  role: 'user' | 'system';
  content: string;
  type?: 'text' | 'image';
  imagePath?: string;
};

function App() {
  const [messages, setMessages] = useState<Message[]>(() => {
    const saved = localStorage.getItem('beantext-messages');
    return saved ? JSON.parse(saved) : [];
  });
  const [input, setInput] = useState('');

  //preload all the images in picturesAndDescriptions
  useEffect(() => {
    picturesAndDescriptions.forEach(p => {
      const img = new Image();
      img.src = p.path;
    });
  }, []);
  
  // Viewport height state for iOS keyboard handling
  const [viewportHeight, setViewportHeight] = useState('100vh');
  const keyboardOpenRef = useRef(false);

  
  const chatRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
  
    const update = () => {
      const keyboardHeight = Math.max(0, window.innerHeight - vv.height);
      const isOpen = keyboardHeight > 50;
  
      keyboardOpenRef.current = isOpen;
  
      document.documentElement.style.setProperty(
        '--keyboard-offset',
        `${keyboardHeight}px`
      );
  
      // 🔑 CRITICAL FIX
      if (!isOpen) {
        chatRef.current?.classList.add('keyboard-closing');
        setTimeout(() => {
          chatRef.current?.classList.remove('keyboard-closing');
        }, 150);
      }
    };
  
    update();
    vv.addEventListener('resize', update);
  
    return () => vv.removeEventListener('resize', update);
  }, []);
  
  const scrollToBottomNow = () => {
    const el = chatRef.current;
    if (!el) return;
  
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
      });
    });
  };
  
  
  

  // 2. Auto-scroll logic
  const scrollToBottom = () => {
    const el = chatRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'auto' });
  };

  useLayoutEffect(() => {
    const el = chatRef.current;
    if (!el) return;
  
    // If keyboard just closed, Safari needs a frame to stabilize layout
    if (!keyboardOpenRef.current) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          el.scrollTo({ top: el.scrollHeight, behavior: 'auto' });
        });
      });
    } else {
      // Normal case
      el.scrollTo({ top: el.scrollHeight, behavior: 'auto' });
    }
  }, [messages]);
  

  const getBeanMessage = async (
    _newMessage: string,
    _previousMessages: Message[]
  ): Promise<[string, string]> => {
    // await new Promise(resolve => setTimeout(resolve, 1200));
    // const randomEntry =
    //   picturesAndDescriptions[
    //     Math.floor(Math.random() * picturesAndDescriptions.length)
    //   ];

    const useProductionUrl = true;
    const apiUrl = useProductionUrl ? 'https://flipify-api.onrender.com/bean' : 'http://127.0.0.1:5000/bean';


    //wuery flipify api 
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        prompt: _newMessage,
        previous_messages: messages,
      }),
    });
    const data = await response.json();

    //get the image path from the key
    const imagePath = picturesAndDescriptions.find(p => p.key === data.key)?.path || 'beanneutral.jpeg';
  
    return [data.description, imagePath];
  };
  

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userMsg = input;
    setInput('');

    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
    
    // Keep focus on input for rapid fire texting
    inputRef.current?.focus();

    const updated: Message[] = [...messages, { role: 'user', content: userMsg }];
    setMessages(updated);
    localStorage.setItem('beantext-messages', JSON.stringify(updated));

    setMessages(prev => [...prev, { role: 'system', content: '/loading' }]);

    try {
      const [beanResponse, imagePath] = await getBeanMessage(userMsg, updated);

setMessages(prev => {
  const filtered = prev.filter(m => m.content !== '/loading');

  const newMsgs: Message[] = [
    ...filtered,
    {
      role: 'system',
      type: 'text',
      content: beanResponse,
    },
    {
      role: 'system',
      type: 'image',
      content: '',
      imagePath,
    },
  ];

  localStorage.setItem('beantext-messages', JSON.stringify(newMsgs));
  return newMsgs;
});

    } catch {
      setMessages(prev => prev.filter(m => m.content !== '/loading'));
    }
  };

  return (
    <>
      <style>{css}</style>
      
      {/* The outer container is fixed to the visual viewport height.
         Inside, we use Flexbox to distribute space.
      */}
      <div 
        className="app-container" 
        // style={{ height: viewportHeight }}
      >
        
        {/* Header - Static Flex Item */}
        <div className="header">
          <div className="avatar">
            <img src="beansayinghi.jpeg" alt="Bean" />
          </div>
          <div className="header-text">
            <h2>Bean</h2>
            <span className="status">Bean is online</span>
          </div>
        </div>

        {/* Chat List - Grows to fill space */}
        <div className="imessage-list" ref={chatRef}>
          <div className="spacer" /> 
          {messages.map((msg, index) => {
            const isLastInGroup = messages[index + 1]?.role !== msg.role;
            const isEmoji =
              /^\p{Extended_Pictographic}$/u.test(msg.content.trim()) && msg.content.trim().length <= 6;
            const bubbleClass =
              msg.role === 'user' ? 'from-me' : 'from-them';

              if (msg.type === 'image' && msg.imagePath) {
                return (
                  <div key={index} className="message-row from-them">
                    <img
                      src={msg.imagePath}
                      alt="Bean"
                      onLoad={scrollToBottomNow}
                      className="image-bubble"
                    />
                  </div>
                );
              }
              

            if (msg.content === '/loading') {
              return (
                <div key={index} className={`message-row from-them`}>
                  <p className="typing-indicator">
                    <span />
                    <span />
                    <span />
                  </p>
                </div>
              );
            }

            return (
              <div 
                key={index} 
                className={`message-row ${bubbleClass} ${isLastInGroup ? 'no-tail' : 'no-tail'}`}
              >
                <p className={`${isEmoji ? 'emoji' : ''}`}>
                  {msg.content}
                </p>
              </div>
            );
          })}
        </div>

        {/* Input Area - Static Flex Item */}
        <form className="input-area" onSubmit={handleSubmit}>
          <input
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder="iMessage"
            // autoCorrect="off"
            autoCapitalize="sentences"
            spellCheck={false}
            autoComplete="off"
            inputMode="text"
            enterKeyHint="send"
          />
          <button type="submit" disabled={!input.trim()} className={input.trim() ? 'active' : ''}>
  ↑
</button>

        </form>
      </div>
    </>
  );
}

export default App;

/* ------------------ */
/* CSS Styles         */
/* ------------------ */

const css = `
@font-face {
  font-family: "SanFrancisco";
  src: url("https://cdn.rawgit.com/AllThingsSmitty/fonts/25983b71/SanFrancisco/sanfranciscodisplay-regular-webfont.woff2") format("woff2");
}
  .imessage-list.keyboard-closing {
  pointer-events: none;
}


:root {
  --app-bg: #fff;
  --header-bg: rgba(245, 245, 247, 0.95);
  --bubble-blue: #007aff;
  --bubble-gray: #e9e9eb;
  --text-primary: #000;
  --text-white: #fff;
}
.image-bubble {
  max-width: 220px;
  border-radius: 18px;
  object-fit: cover;
}
.image-bubble {
  border-bottom-left-radius: 4px;
}

* {
  box-sizing: border-box;
}

body {
  margin: 0;
  padding: 0;
  font-family: -apple-system, BlinkMacSystemFont, "SanFrancisco", "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  background-color: var(--app-bg);
  overflow: hidden; /* Prevent body scroll, let app-container handle it */
  overscroll-behavior-y: none; /* Prevent rubber banding on body */
}

/* Main Layout Wrapper 
   Positioned fixed to lock to the viewport (handles keyboard slide)
*/
.app-container {
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  background: var(--app-bg);
  overflow: hidden;
}



/* Header */
.header {
  flex: 0 0 auto; /* Don't grow or shrink */
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding-top: max(env(safe-area-inset-top), 10px);
  padding-bottom: 10px;
  background: var(--header-bg);
  border-bottom: 1px solid #c6c6c8;
  backdrop-filter: blur(10px);
  z-index: 10;
}

.header .avatar {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  overflow: hidden;
  margin-bottom: 4px;
}

.header img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.header-text {
  text-align: center;
}

.header h2 {
  font-size: 14px;
  font-weight: 500;
  margin: 0;
  color: #000;
}

.header .status {
  font-size: 12px;
  color: #8e8e93;
}

/* Scrollable Message List 
   Takes up all remaining space
*/
.imessage-list {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  padding: 1rem;
  padding-bottom: calc(
    1rem + 
    56px +               /* input height */
    var(--keyboard-offset, 0px)
  );

  display: flex;
  flex-direction: column;
  gap: 0.2rem;

  touch-action: pan-y; /* IMPORTANT */
}


.spacer {
  flex: 1; /* Pushes messages to bottom if there are few */
  min-height: 1rem;
}

/* Message Rows */
.message-row {
  display: flex;
  width: 100%;
  margin-bottom: 2px;
}

.message-row.from-me {
  justify-content: flex-end;
}

.message-row.from-them {
  justify-content: flex-start;
}

/* Bubbles */
.message-row p {
  max-width: 75%;
  margin: 0;
  padding: 8px 16px;
  border-radius: 18px;
  font-size: 16px;
  line-height: 1.3;
  position: relative;
  word-wrap: break-word;
}

/* Blue Bubble */
.from-me p {
  background-color: var(--bubble-blue);
  color: var(--text-white);
  border-bottom-right-radius: 4px; /* Default tail spot */
}

/* Gray Bubble */
.from-them p {
  background-color: var(--bubble-gray);
  color: var(--text-primary);
  border-bottom-left-radius: 4px; /* Default tail spot */
}

.emoji {
  background: none !important;
  font-size: 3rem !important;
  padding: 0 !important;
}
.emoji::before { display: none !important; }

/* Typing Indicator */
.typing-indicator span {
  height: 8px;
  width: 8px;
  margin: 0 2px;
  background-color: #999;
  border-radius: 50%;
  display: inline-block;
  opacity: 0.4;
  animation: blink 1s infinite;
}
.typing-indicator span:nth-child(2) { animation-delay: 0.2s; }
.typing-indicator span:nth-child(3) { animation-delay: 0.4s; }
@keyframes blink { 50% { opacity: 1; } }

/* Input Area */
.input-area {
  padding: 8px 12px;
  padding-bottom: max(env(safe-area-inset-bottom), 12px);
  background: #f5f5f7;
  border-top: 1px solid #c6c6c8;
  display: flex;
  align-items: center;
  gap: 10px;

}


.input-area input {
  flex: 1;
  border: 1px solid #c6c6c8;
  border-radius: 18px;
  padding: 8px 14px;
  font-size: 16px; /* CRITICAL: Prevents iOS zoom */
  line-height: 20px;
  outline: none;
  background: #fff;
  min-height: 36px;
}

.input-area button {
  background-color: var(--bubble-blue);
  color: white;
  border: none;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s;
}

.input-area button:disabled {
  background-color: #999;
}
`;